import axios from 'axios';
import { CertificateInfo, SubmitCertificateResponse, ApiResponse } from '../types/certificate';
import appConfig from '../config/app.config';

/**
 * 合格证服务层
 * 处理与后端API的通信
 */

// API基础URL配置
const API_BASE_URL = appConfig.API_BASE_URL;

// 创建axios实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30秒超时
  headers: {
    'Content-Type': 'application/json',
  }
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config: any) => {
    // 可以在这里添加认证token等
    console.log('发送请求:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error: any) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response: any) => {
    console.log('收到响应:', response.status, response.config.url);
    return response;
  },
  (error: any) => {
    console.error('响应错误:', error);
    
    // 统一错误处理
    if (error.response) {
      // 服务器返回错误状态码
      const { status, data } = error.response;
      switch (status) {
        case 400:
          throw new Error(data.message || '请求参数错误');
        case 401:
          throw new Error('未授权访问');
        case 403:
          throw new Error('没有权限');
        case 404:
          throw new Error('接口不存在');
        case 500:
          throw new Error('服务器内部错误');
        default:
          throw new Error(data.message || `请求失败 (${status})`);
      }
    } else if (error.request) {
      // 网络错误
      throw new Error('网络连接失败，请检查网络设置');
    } else {
      // 其他错误
      throw new Error(error.message || '未知错误');
    }
  }
);

/**
 * 合格证服务类
 */
export class CertificateService {
  /**
   * 提交合格证信息并生成合格证
   */
  static async submitCertificate(data: CertificateInfo): Promise<SubmitCertificateResponse> {
    try {
      const response = await apiClient.post<SubmitCertificateResponse>('/certificates', data);
      return response.data;
    } catch (error) {
      console.error('提交合格证失败:', error);
      throw error;
    }
  }

  /**
   * 获取合格证列表
   */
  static async getCertificates(page = 1, limit = 10): Promise<ApiResponse> {
    try {
      const response = await apiClient.get<ApiResponse>('/certificates', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('获取合格证列表失败:', error);
      throw error;
    }
  }

  /**
   * 获取单个合格证详情
   */
  static async getCertificateById(id: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.get<ApiResponse>(`/certificates/${id}`);
      return response.data;
    } catch (error) {
      console.error('获取合格证详情失败:', error);
      throw error;
    }
  }

  /**
   * 删除合格证
   */
  static async deleteCertificate(id: string): Promise<ApiResponse> {
    try {
      const response = await apiClient.delete<ApiResponse>(`/certificates/${id}`);
      return response.data;
    } catch (error) {
      console.error('删除合格证失败:', error);
      throw error;
    }
  }

  /**
   * 下载PDF文件
   */
  static async downloadPDF(url: string, filename?: string): Promise<void> {
    try {
      const response = await apiClient.get(url, {
        responseType: 'blob'
      });

      // 创建下载链接
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || `合格证_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // 清理
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('下载PDF失败:', error);
      throw error;
    }
  }

  /**
   * 检查服务器连接状态
   */
  static async checkHealth(): Promise<boolean> {
    try {
      const response = await apiClient.get('/health');
      return response.status === 200;
    } catch (error) {
      console.error('服务器连接检查失败:', error);
      return false;
    }
  }
}

/**
 * 模拟服务 - 用于开发和测试
 */
export class MockCertificateService {
  /**
   * 模拟提交合格证
   */
  static async submitCertificate(data: CertificateInfo): Promise<SubmitCertificateResponse> {
    // 模拟API延迟
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const mockResponse: SubmitCertificateResponse = {
      success: true,
      data: {
        certificateId: `CERT_${Date.now()}`,
        pdfUrl: `/api/files/certificate_${Date.now()}.pdf`,
        qrCodeUrl: `/api/files/qrcode_${Date.now()}.png`,
        createdAt: new Date().toISOString()
      },
      message: '合格证生成成功'
    };

    console.log('模拟生成合格证:', mockResponse);
    return mockResponse;
  }
}

// 根据环境决定使用真实服务还是模拟服务
const isDevelopment = import.meta.env.MODE === 'development';
export const certificateService = isDevelopment ? MockCertificateService : CertificateService;